import React, { useContext, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { PrayerPartnerContext } from "../contexts/PrayerPartnerContext";
import { ProfileContext } from "../contexts/ProfileContext";
import { blockReasons } from "../assets/data";
import { ToastContainer } from 'react-toastify';
import { showErrorMessage, showSuccessMessage } from "../helpers/useToast";

const PAGE_SIZE_OPTIONS = [10, 25, 50];

const formatDisplayDate = (value) => {
    if (value == null || value === "") return "—";
    const d = new Date(typeof value === "string" ? value.replace(" ", "T") : value);
    return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleString();
};

/** Local calendar date only (midnight boundary). */
const startOfLocalDay = (d) =>
    new Date(d.getFullYear(), d.getMonth(), d.getDate());

/**
 * Unblock only when the block’s expiry is still on a future calendar day.
 * If valid_until is today or earlier (or missing/invalid), Unblock is disabled.
 */
const canUnblockBlacklistRow = (row) => {
    const raw = row.valid_until;
    if (raw == null || raw === "") return false;
    const parsed = new Date(typeof raw === "string" ? raw.replace(" ", "T") : raw);
    if (Number.isNaN(parsed.getTime())) return false;
    const validDay = startOfLocalDay(parsed);
    const today = startOfLocalDay(new Date());
    return validDay.getTime() > today.getTime();
};

const digitsOnly = (s) => String(s || "").replace(/\D/g, "");

const rowMatchesNumberFilter = (row, query) => {
    const q = query.trim();
    if (!q) return true;
    const qDigits = digitsOnly(q);
    const phone = row.phone_number || "";
    if (qDigits.length > 0) {
        return digitsOnly(phone).includes(qDigits);
    }
    return phone.toLowerCase().includes(q.toLowerCase());
};

const rowMatchesReasonFilter = (row, selectedValue) => {
    if (!selectedValue) return true;
    const stored = String(row.block_reason || "").trim();
    return stored === String(selectedValue).trim();
};

export const BlockCallerPage = ( ) => {
    const { worker } = useContext(ProfileContext);
    const {
        blockCaller,
        fetchBlacklistDetails,
        blacklistDetails,
        blacklistDetailsLoading,
    } = useContext(PrayerPartnerContext);

    const canViewBlockedCallerList =
        worker?.role === "super_admin" ||
        worker?.role === "prayer_centre_admin";
    const [phoneNumber, setPhoneNumber] = useState("");
    const [blockReason, setBlockReason] = useState("");
    const [filterNumber, setFilterNumber] = useState("");
    const [filterReason, setFilterReason] = useState("");
    const [listPage, setListPage] = useState(1);
    const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[0]);
    const [unblockingRowKey, setUnblockingRowKey] = useState(null);

    useEffect(() => {
        if (canViewBlockedCallerList) {
            fetchBlacklistDetails();
        }
    }, [fetchBlacklistDetails, canViewBlockedCallerList]);

    useEffect(() => {
        setListPage(1);
    }, [filterNumber, filterReason, pageSize]);

    const filteredRows = useMemo(
        () =>
            blacklistDetails.filter(
                (row) =>
                    rowMatchesNumberFilter(row, filterNumber) &&
                    rowMatchesReasonFilter(row, filterReason),
            ),
        [blacklistDetails, filterNumber, filterReason],
    );

    const totalFiltered = filteredRows.length;
    const totalPages = Math.max(1, Math.ceil(totalFiltered / pageSize) || 1);

    useEffect(() => {
        if (listPage > totalPages) setListPage(totalPages);
    }, [listPage, totalPages]);

    const paginatedRows = useMemo(() => {
        const start = (listPage - 1) * pageSize;
        return filteredRows.slice(start, start + pageSize);
    }, [filteredRows, listPage, pageSize]);

    const rangeStart = totalFiltered === 0 ? 0 : (listPage - 1) * pageSize + 1;
    const rangeEnd = totalFiltered === 0 ? 0 : Math.min(listPage * pageSize, totalFiltered);

    const handleSubmit = async(e) => {
        e.preventDefault();
        // Ensure both phone number and block reason are selected
        if (phoneNumber && blockReason) {
            let phone_number = "+" + phoneNumber;
            let block_reason = JSON.parse(blockReason)
          // Call the blockCaller API with the selected data
            const result =  await blockCaller({
                phone_number,
                blockdelay: block_reason.delay,
                blockreason: block_reason.reason,
            });
            if (result.data.success){
                showSuccessMessage(result.data.message);
                if (canViewBlockedCallerList) {
                    await fetchBlacklistDetails();
                }
            }
            else {
                showErrorMessage('Error occured');
            }
            // Reset the form
            setPhoneNumber("");
            setBlockReason("");
        } else {
            showErrorMessage('Please fill in all fields');
        }
    };

    const rowActionKey = (row) => String(row.id ?? row.phone_number ?? "");

    const handleUnblockRow = async (row) => {
        const phone_number = row.phone_number;
        if (!phone_number) {
            showErrorMessage("Missing phone number for this row.");
            return;
        }
        const key = rowActionKey(row);
        setUnblockingRowKey(key);
        try {
            const result = await blockCaller({
                phone_number,
                blockdelay: 0,
                ...(row.block_reason
                    ? { blockreason: row.block_reason }
                    : {}),
            });
            if (result?.error) {
                showErrorMessage("Unblock failed.");
            } else if (result?.data?.success) {
                showSuccessMessage(result.data.message || "Caller unblocked.");
                if (canViewBlockedCallerList) {
                    await fetchBlacklistDetails();
                }
            } else {
                showErrorMessage(result?.data?.message || "Unblock failed.");
            }
        } catch {
            showErrorMessage("Unblock failed.");
        } finally {
            setUnblockingRowKey(null);
        }
    };

    return (
        <Wrapper>
            <ToastContainer/>
            <header className="mb-3">
                <h1>Block a caller Number</h1>
            </header>

            <BlockForm className="m-4 p-4 rounded" onSubmit={handleSubmit}>  
                <div>
                    <label htmlFor="phone_number" className="block text-sm font-medium text-gray-700 mb-2" >Enter Phone Number to block: </label>
                    <div >
                        <input
                        type="tel"
                        pattern="[0-9]{11}"
                        id="phone_number"
                        placeholder="12899229822"
                        name="phone_number"
                        maxLength={11}
                        minLength={11}
                        value={phoneNumber}
                        onChange={(e) => setPhoneNumber(e.target.value)}
                        className="block w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        required
                        />
                    </div>
                    <small className="text-gray-500">Phone number should be 11 digits, e.g., 16476474456</small>

                  
                    {/* <input type="tel" pattern="[0-9]{10}"
                        id="phone_number" className="border"
                        value={phoneNumber} placeholder="12899229822" 
                        onChange={(e) => setPhoneNumber(e.target.value)}  required/>        */}
                </div>
                
                <div>
                    <label htmlFor="blockReason" className="block text-sm font-medium text-gray-700 mb-2 mt-4" >Reason For Block: </label>
                    <select className="form-select" aria-label="Reason For Block"  id="blockReason" value={blockReason}
                        onChange={(e)=> setBlockReason(e.target.value)}>
                            <option value="" disabled>Select a reason</option>
                            { 
                            blockReasons.map((blockReason) => {
                                return (
                                    <option key={blockReason.id+blockReason.reason} value={JSON.stringify(blockReason)}>{blockReason.reason}</option>
                                );
                            })
                            }
                    </select>  
                </div>

                <button type="submit" className="mt-4 px-4 py-2 rounded border">
                    Block this Number
                </button>
            </BlockForm>

            {canViewBlockedCallerList ? (
            <ListSection className="m-4 p-4 rounded">
                <ListHeader>
                    <h2>Blocked caller numbers</h2>
                    <RefreshButton
                        type="button"
                        onClick={() => fetchBlacklistDetails()}
                        disabled={blacklistDetailsLoading}
                    >
                        {blacklistDetailsLoading ? "Loading…" : "Refresh list"}
                    </RefreshButton>
                </ListHeader>

                <FilterBar>
                    <FilterField>
                        <FilterLabel htmlFor="filter_phone">Filter by number</FilterLabel>
                        <FilterInput
                            id="filter_phone"
                            type="text"
                            placeholder="Digits or full number (e.g. 728 or +1313…)"
                            value={filterNumber}
                            onChange={(e) => setFilterNumber(e.target.value)}
                            disabled={blacklistDetailsLoading && blacklistDetails.length === 0}
                        />
                    </FilterField>
                    <FilterField>
                        <FilterLabel htmlFor="filter_reason">Filter by reason</FilterLabel>
                        <FilterSelect
                            id="filter_reason"
                            value={filterReason}
                            onChange={(e) => setFilterReason(e.target.value)}
                            disabled={blacklistDetailsLoading && blacklistDetails.length === 0}
                        >
                            <option value="">All reasons</option>
                            {blockReasons.map((br) => (
                                <option key={br.id} value={br.value}>
                                    {br.reason}
                                </option>
                            ))}
                        </FilterSelect>
                    </FilterField>
                    <FilterField $narrow>
                        <FilterLabel htmlFor="page_size">Rows per page</FilterLabel>
                        <PageSizeSelect
                            id="page_size"
                            value={pageSize}
                            onChange={(e) => setPageSize(Number(e.target.value))}
                        >
                            {PAGE_SIZE_OPTIONS.map((n) => (
                                <option key={n} value={n}>
                                    {n}
                                </option>
                            ))}
                        </PageSizeSelect>
                    </FilterField>
                </FilterBar>

                {blacklistDetailsLoading && blacklistDetails.length === 0 ? (
                    <p className="text-gray-600 text-sm">Loading blocked numbers…</p>
                ) : blacklistDetails.length === 0 ? (
                    <p className="text-gray-600 text-sm">No blocked numbers returned from the server.</p>
                ) : (
                    <>
                        <ResultMeta>
                            Showing{" "}
                            <strong>
                                {rangeStart}–{rangeEnd}
                            </strong>{" "}
                            of <strong>{totalFiltered}</strong>
                            {totalFiltered !== blacklistDetails.length ? (
                                <span className="muted">
                                    {" "}
                                    ({blacklistDetails.length} total loaded)
                                </span>
                            ) : null}
                        </ResultMeta>
                        <TableWrap>
                            <table className="w-full text-sm">
                                <thead>
                                    <tr>
                                        <th>Phone number</th>
                                        <th>Reason</th>
                                        <th>Valid until</th>
                                        <th>Created</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {paginatedRows.length === 0 ? (
                                        <tr>
                                            <td colSpan={5} className="empty-cell">
                                                No rows match your filters.
                                            </td>
                                        </tr>
                                    ) : (
                                        paginatedRows.map((row) => {
                                            const aKey = rowActionKey(row);
                                            const busy = unblockingRowKey === aKey;
                                            const unblockAllowed =
                                                canUnblockBlacklistRow(row);
                                            return (
                                                <tr key={row.id ?? row.phone_number}>
                                                    <td>{row.phone_number}</td>
                                                    <td>{row.block_reason || "—"}</td>
                                                    <td>{formatDisplayDate(row.valid_until)}</td>
                                                    <td>{formatDisplayDate(row.created_at)}</td>
                                                    <td>
                                                        <UnblockButton
                                                            type="button"
                                                            onClick={() => handleUnblockRow(row)}
                                                            disabled={
                                                                busy ||
                                                                blacklistDetailsLoading ||
                                                                !unblockAllowed
                                                            }
                                                            title={
                                                                unblockAllowed
                                                                    ? "Remove block (upsert via API)"
                                                                    : "Unblock is only available when valid until is after today."
                                                            }
                                                        >
                                                            {busy ? "…" : "Unblock"}
                                                        </UnblockButton>
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    )}
                                </tbody>
                            </table>
                        </TableWrap>
                        <PaginationBar>
                            <PageNavButton
                                type="button"
                                onClick={() => setListPage((p) => Math.max(1, p - 1))}
                                disabled={listPage <= 1 || totalFiltered === 0}
                            >
                                Previous
                            </PageNavButton>
                            <PageInfo>
                                Page {totalFiltered === 0 ? 1 : listPage} of {totalPages}
                            </PageInfo>
                            <PageNavButton
                                type="button"
                                onClick={() => setListPage((p) => Math.min(totalPages, p + 1))}
                                disabled={listPage >= totalPages || totalFiltered === 0}
                            >
                                Next
                            </PageNavButton>
                        </PaginationBar>
                    </>
                )}
            </ListSection>
            ) : null}
        </Wrapper>
    );
}

const Wrapper = styled.div`
    width: 100%;
    header {
        text-align: center;
        background-color: #F1F1F1;
        color: #178B96;
        font-family: Futura PT;
    }
`;

const BlockForm = styled.form`
    border: 1px solid #178B96;

    button{
        background-color: #FCD5C9;
        color: #EC3310;
    };
`;

const ListSection = styled.section`
    border: 1px solid #178B96;
    background: #fff;
`;

const ListHeader = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    margin-bottom: 1rem;

    h2 {
        margin: 0;
        font-size: 1.125rem;
        color: #178b96;
        font-family: Futura PT, sans-serif;
    }
`;

const RefreshButton = styled.button`
    padding: 0.35rem 0.75rem;
    border: 1px solid #178b96;
    border-radius: 0.25rem;
    background: #f1f1f1;
    color: #178b96;
    font-size: 0.875rem;
    cursor: pointer;

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
`;

const TableWrap = styled.div`
    overflow-x: auto;

    table {
        border-collapse: collapse;
    }

    th,
    td {
        border: 1px solid #d1d5db;
        padding: 0.5rem 0.75rem;
        text-align: left;
        vertical-align: top;
    }

    th {
        background: #f1f1f1;
        color: #178b96;
        font-weight: 600;
    }

    tbody tr:nth-child(even) {
        background: #fafafa;
    }

    .empty-cell {
        text-align: center;
        color: #6b7280;
        padding: 1rem;
    }
`;

const FilterBar = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 1rem;
    margin-bottom: 1rem;
    align-items: flex-end;
`;

const FilterField = styled.div`
    flex: 1 1 12rem;
    min-width: 10rem;

    ${(p) =>
        p.$narrow &&
        `
        flex: 0 0 auto;
        min-width: 7rem;
    `}
`;

const FilterLabel = styled.label`
    display: block;
    font-size: 0.75rem;
    font-weight: 600;
    color: #374151;
    margin-bottom: 0.25rem;
`;

const FilterInput = styled.input`
    width: 100%;
    padding: 0.4rem 0.5rem;
    border: 1px solid #d1d5db;
    border-radius: 0.25rem;
    font-size: 0.875rem;

    &:disabled {
        background: #f3f4f6;
    }
`;

const PageSizeSelect = styled.select`
    width: 100%;
    padding: 0.4rem 0.5rem;
    border: 1px solid #d1d5db;
    border-radius: 0.25rem;
    font-size: 0.875rem;
    background: #fff;
`;

const FilterSelect = styled.select`
    width: 100%;
    padding: 0.4rem 0.5rem;
    border: 1px solid #d1d5db;
    border-radius: 0.25rem;
    font-size: 0.875rem;
    background: #fff;
`;

const ResultMeta = styled.p`
    margin: 0 0 0.75rem;
    font-size: 0.875rem;
    color: #374151;

    .muted {
        color: #6b7280;
        font-weight: normal;
    }
`;

const PaginationBar = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: center;
    gap: 1rem;
    margin-top: 1rem;
    padding-top: 0.75rem;
    border-top: 1px solid #e5e7eb;
`;

const PageNavButton = styled.button`
    padding: 0.35rem 0.85rem;
    border: 1px solid #178b96;
    border-radius: 0.25rem;
    background: #fff;
    color: #178b96;
    font-size: 0.875rem;
    cursor: pointer;

    &:disabled {
        opacity: 0.45;
        cursor: not-allowed;
    }
`;

const PageInfo = styled.span`
    font-size: 0.875rem;
    color: #374151;
`;

const UnblockButton = styled.button`
    padding: 0.25rem 0.6rem;
    border: 1px solid #b45309;
    border-radius: 0.25rem;
    background: #fffbeb;
    color: #b45309;
    font-size: 0.8125rem;
    cursor: pointer;
    white-space: nowrap;

    &:disabled {
        opacity: 0.55;
        cursor: not-allowed;
    }
`;